import React, { useEffect, useState } from 'react';
import { fetchData, saveData } from '../../../nextgen/service/dataServices';
import { Button, Drawer, Popconfirm, Space, Table, Tag } from 'antd';
import { pageSizeOptions } from '../../../nextgen/components/common-constants';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import { CalendarOutlined, QuestionCircleOutlined, UserOutlined } from '@ant-design/icons';
import ChangeDetails from './ChangeDetails';

function ActivityPage({ from = null, to = null, tableName = null, entityId = null, fetchDataPath, refetchTrigger = null }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedChangeRecord, setSelectedChangeRecord] = useState(null);
  const history = useHistory();

  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    from: from,
    to: to,
    tableName: tableName,
    entityId: entityId
  });

  useEffect(() => {
    setFilters(prevState => {
      return {
        ...prevState,
        from: from,
        to: to
      }
    });
  }, [from, to])

  const dataFetcher = fetchData(fetchDataPath, data => setData(data.content), filters);
  const mutation = saveData('/api/audit/revert', 'POST');

  useEffect(() => {
    if (refetchTrigger) {
      dataFetcher.refetch();
    }
  }, [refetchTrigger])

  function getActionColorStyle(action) {
    switch (action) {
      case 'INSERT':
        return {
          backgroundColor: '#dcfce7',
          color: '#166534',
          borderColor: '#bbf7d0',
        }
      case 'UPDATE':
        return {
          backgroundColor: '#dbeafe',
          color: '#1e40af',
          borderColor: '#bfdbfe',
        }
      case 'DELETE':
        return {
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderColor: '#e5e7eb',
        }
      default:
        return {};
    }
  }

  function handleSelectedRecord(record) {
    setSelectedChangeRecord(record);
    setIsDrawerOpen(true);
  }

  function getTableRoute(type) {
    switch (type) {
      case "object_overview":
        return "save-object"
      case "condition":
        return "save-condition"
      case "curatorial_form":
        return "save-curatorial-form"
      case "technical_form":
        return "save-technical-form"
      case "movement":
        return "save-movement"
      case "treatment":
        return "save-treatment"
      case "insurance":
        return "save-insurance"
    }
  }

  const renderEntityId = (text, row) => {
    if (row.action === "DELETE") {
      return (<Button type='link' disabled>{text}</Button>)
    }
    return (
      <Button
        type="link"
        onClick={() => history.push(`/${getTableRoute(row.tableName)}/${row.recordId}`)}
      >
        {text}
      </Button>
    )
  }

  const columns = [
    {
      title: t('activity.action'),
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => (
        <Tag
          style={
            getActionColorStyle(record.action)
          }
        >
          {text.toUpperCase()}
        </Tag >
      ),
    },
    {
      title: (<Space><UserOutlined />{t('activity.user')}</Space>),
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: t('activity.entityType'),
      dataIndex: 'tableName',
      key: 'tableName',
    },
    {
      title: t('activity.entityId'),
      dataIndex: 'recordId',
      key: 'recordId',
      render: renderEntityId,
    },
    {
      title: t('activity.identifierName'),
      dataIndex: "identifierName",
      key: "identifierName",
    },
    {
      title: (<Space><CalendarOutlined />{t('activity.changedAt')}</Space>),
      dataIndex: 'changedAt',
      key: 'changedAt',
      render: text => formatDate(text)
    },
    {
      title: t('activity.viewDetails'),
      dataIndex: 'id',
      key: 'id',
      render: (text, record) => (
        <Button onClick={() => handleSelectedRecord(record)}>{t('activity.details')}</Button>
      )
    },
  ];

  const handleTableChange = pagination => {
    if (pagination.current - 1 !== filters.page) {
      setFilters(prevState => ({
        ...prevState,
        page: pagination.current - 1,
      }));
    }
    if (pagination.pageSize !== filters?.size) {
      setFilters(prevState => ({
        ...prevState,
        size: pagination.pageSize,
      }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  function revert() {
    mutation.mutate(
      { dataToSend: { changeId: selectedChangeRecord.id } },
      { onSuccess: dataFetcher.refetch() },
    );
    setIsDrawerOpen(false);
  }

  const undoButton = selectedChangeRecord && (
    <Popconfirm
      title={t('activity.confirmation')}
      description={t('activity.revertConfirm')}
      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
      onConfirm={revert}
      okButtonProps={{ size: 'middle' }}
      okText={t('activity.yes')}
      cancelButtonProps={{ size: 'middle' }}
      styles={{ body: { padding: 16 } }}
    >
      <Button type='primary'>{t('activity.revertChange')}</Button>
    </Popconfirm>
  )

  return (
    <>
      <Table
        dataSource={data}
        columns={columns}
        loading={dataFetcher.isFetching}
        rowKey={record => record.id}
        onChange={handleTableChange}
        size='middle'
        pagination={{
          pageSize: filters?.size,
          total: dataFetcher.data ? dataFetcher.data.data.totalElements : 0,
          showSizeChanger: true,
          current: filters.page + 1,
          pageSizeOptions: pageSizeOptions,
          showTotal: (total, range) => {
            return t('common.paginationTotal', { start: range[0], end: range[1], total });
          },
        }}
      />
      <Drawer
        title={t('activity.changedFields')}
        size='large'
        closable={true}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedChangeRecord(null);
        }}
        open={isDrawerOpen}
        destroyOnHidden
        width={920}
        extra={undoButton}
      >
        {selectedChangeRecord && <ChangeDetails record={selectedChangeRecord} />}
      </Drawer >
    </>

  );
}

export default ActivityPage;
